import { useState, type MouseEvent as ReactMouseEvent } from "react";
import { polygonToPath, polygonBbox, type Bbox } from "../lib/geo";
import { MapPin } from "./MapPin";
import type { MapPinSymbol } from "./MapPin";
import "./GardenMap.css";

export interface GardenMapPin {
  id: string;
  x: number;
  y: number;
  symbol: MapPinSymbol;
  /** Parent location id (used in edit mode to clamp drag inside the bed bbox). */
  locationId?: string;
}

export interface GardenMapLocation {
  id: string;
  name: string;
  kind: "bed" | "container";
  polygon: ReadonlyArray<readonly [number, number]> | null;
}

interface GardenMapProps {
  garden: { id: string; viewport: { width: number; height: number } | null };
  locations: ReadonlyArray<GardenMapLocation>;
  pins?: ReadonlyArray<GardenMapPin>;
  selectedPinId?: string | null;
  mode?: "view" | "edit";
  onPinClick?: (plantId: string) => void;
  onMapClick?: () => void;
  /** Edit-mode click on an empty area, in viewport coordinates. */
  onCanvasPlace?: (point: { x: number; y: number }) => void;
  /** Edit-mode pin drop, in viewport coordinates (already clamped to parent bbox). */
  onPinDrop?: (plantId: string, point: { x: number; y: number }) => void;
}

const DEFAULT_WIDTH = 700;
const DEFAULT_HEIGHT = 660;

const BOUNDARY_PATH =
  "M 50 40 L 658 48 Q 663 53 658 58 L 654 612 Q 649 617 644 615 L 56 620 Q 47 615 49 605 Z";

function clampToBbox(x: number, y: number, bbox: Bbox): { x: number; y: number } {
  return {
    x: Math.max(bbox.minX, Math.min(bbox.maxX, x)),
    y: Math.max(bbox.minY, Math.min(bbox.maxY, y)),
  };
}

function svgPointFromMouse(event: ReactMouseEvent<SVGSVGElement>): { x: number; y: number } | null {
  const svg = event.currentTarget;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const { x, y } = pt.matrixTransform(ctm.inverse());
  return { x, y };
}

export function GardenMap({
  garden,
  locations,
  pins = [],
  selectedPinId,
  mode = "view",
  onPinClick,
  onMapClick,
  onCanvasPlace,
  onPinDrop,
}: GardenMapProps) {
  const vw = garden.viewport?.width ?? DEFAULT_WIDTH;
  const vh = garden.viewport?.height ?? DEFAULT_HEIGHT;
  const isDefaultViewport = vw === DEFAULT_WIDTH && vh === DEFAULT_HEIGHT;
  const editing = mode === "edit";

  const locationsWithPolygons = locations.filter((loc) => loc.polygon != null);
  const bboxByLocation = new Map<string, Bbox>(
    locationsWithPolygons.map((loc) => [loc.id, polygonBbox(loc.polygon!)]),
  );

  const [dragPreview, setDragPreview] = useState<{ id: string; x: number; y: number } | null>(null);

  const renderPins = pins.map((pin) =>
    dragPreview && dragPreview.id === pin.id ? { ...pin, x: dragPreview.x, y: dragPreview.y } : pin,
  );

  const clampForPin = (pin: GardenMapPin, x: number, y: number): { x: number; y: number } => {
    const bbox = pin.locationId ? bboxByLocation.get(pin.locationId) : undefined;
    if (!bbox) return { x, y };
    return clampToBbox(x, y, bbox);
  };

  const handleSvgClick = (e: ReactMouseEvent<SVGSVGElement>) => {
    if (editing && onCanvasPlace) {
      const pt = svgPointFromMouse(e);
      if (pt) onCanvasPlace(pt);
      return;
    }
    onMapClick?.();
  };

  const handlePinDragMove = (pin: GardenMapPin, x: number, y: number) => {
    const c = clampForPin(pin, x, y);
    setDragPreview({ id: pin.id, x: c.x, y: c.y });
  };

  const handlePinDragEnd = (pin: GardenMapPin, x: number, y: number) => {
    const c = clampForPin(pin, x, y);
    setDragPreview(null);
    onPinDrop?.(pin.id, c);
  };

  return (
    <div className={`GardenMap${editing ? " GardenMap--edit" : ""}`}>
      <svg
        viewBox={`0 0 ${vw} ${vh}`}
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height="100%"
        onClick={handleSvgClick}
        style={editing ? { cursor: "crosshair" } : undefined}
      >
        <defs>
          <filter id="gmGrain">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves={2} seed={3} result="t" />
            <feColorMatrix in="t" values="0 0 0 0 0.36  0 0 0 0 0.29  0 0 0 0 0.23  0 0 0 0.04 0" />
            <feComposite in2="SourceGraphic" operator="in" />
          </filter>
          <filter id="gmRough" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves={2} seed={3} />
            <feDisplacementMap in="SourceGraphic" scale={1.6} />
          </filter>
          <radialGradient id="gmLawn" cx="50%" cy="55%" r="60%">
            <stop offset="0%" stopColor="#c8d4a8" stopOpacity="0.55" />
            <stop offset="70%" stopColor="#b5c2a3" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#a8b89a" stopOpacity="0.2" />
          </radialGradient>
          <linearGradient id="gmBed" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e8d8b5" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#d4bf95" stopOpacity="0.25" />
          </linearGradient>
          <pattern id="gmPatio" patternUnits="userSpaceOnUse" width="22" height="22">
            <rect width="22" height="22" fill="#d8c8a8" fillOpacity="0.3" />
            <line x1="0" y1="11" x2="22" y2="11" stroke="#8b7a5a" strokeWidth="0.4" strokeOpacity="0.4" />
            <line x1="11" y1="0" x2="11" y2="22" stroke="#8b7a5a" strokeWidth="0.4" strokeOpacity="0.4" />
          </pattern>
          <pattern id="gmPath" patternUnits="userSpaceOnUse" width="6" height="6">
            <rect width="6" height="6" fill="#e8dcc4" fillOpacity="0.4" />
            <circle cx="1.5" cy="2" r="0.5" fill="#8b7a5a" opacity="0.5" />
            <circle cx="4" cy="4" r="0.4" fill="#8b7a5a" opacity="0.4" />
          </pattern>
        </defs>{/* plant-pin <symbol>s live in <MapSpritesGlobal /> mounted by Layout */}

        {/* Background paper grain */}
        <rect width={vw} height={vh} fill="var(--paper, #faf6ef)" />
        <rect width={vw} height={vh} fill="#5d4a3a" fillOpacity="0.025" filter="url(#gmGrain)" />

        {/* Garden boundary — only rendered when using the default 700×660 viewport */}
        {isDefaultViewport && (
          <path
            d={BOUNDARY_PATH}
            fill="none"
            stroke="var(--soil, #5d4a3a)"
            strokeWidth="2.2"
            strokeLinejoin="round"
            filter="url(#gmRough)"
          />
        )}

        {/* Location polygons */}
        {locationsWithPolygons.length === 0 ? (
          <text
            x={vw / 2}
            y={vh / 2}
            textAnchor="middle"
            fontFamily="var(--font-hand, Caveat, cursive)"
            fontSize="24"
            fill="var(--ink-muted, #6b7a6f)"
          >
            Import your garden polygon to get started
          </text>
        ) : (
          locationsWithPolygons.map((loc) => {
            const d = polygonToPath(loc.polygon!);
            if (loc.name === "Lawn") {
              return (
                <path
                  key={loc.id}
                  d={d}
                  fill="url(#gmLawn)"
                  stroke="#7a8c6e"
                  strokeWidth="0.8"
                  strokeOpacity="0.4"
                  filter="url(#gmRough)"
                />
              );
            }
            if (loc.name === "Patio") {
              return (
                <path
                  key={loc.id}
                  d={d}
                  fill="url(#gmPatio)"
                  stroke="#8b7a5a"
                  strokeWidth="0.9"
                  strokeDasharray="2 2"
                />
              );
            }
            if (loc.kind === "container") {
              return (
                <path
                  key={loc.id}
                  d={d}
                  fill="#c89070"
                  opacity="0.55"
                  stroke="var(--soil, #5d4a3a)"
                  strokeWidth="1.2"
                />
              );
            }
            return (
              <path
                key={loc.id}
                d={d}
                fill="url(#gmBed)"
                stroke="var(--soil, #5d4a3a)"
                strokeWidth="1.4"
                filter="url(#gmRough)"
              />
            );
          })
        )}

        {renderPins.map((pin) => {
          const draggable = editing && Boolean(pin.locationId);
          return (
            <MapPin
              key={pin.id}
              x={pin.x}
              y={pin.y}
              symbol={pin.symbol}
              selected={pin.id === selectedPinId}
              draggable={draggable}
              onClick={
                editing
                  ? undefined
                  : onPinClick
                    ? () => onPinClick(pin.id)
                    : undefined
              }
              onDragMove={draggable ? (x, y) => handlePinDragMove(pin, x, y) : undefined}
              onDragEnd={draggable ? (x, y) => handlePinDragEnd(pin, x, y) : undefined}
            />
          );
        })}

        {locationsWithPolygons.length > 0 && locationsWithPolygons.map((loc) => {
          const bbox = polygonBbox(loc.polygon!);
          const cx = (bbox.minX + bbox.maxX) / 2;
          const cy = (bbox.minY + bbox.maxY) / 2;

          if (loc.name === "Lawn") {
            return (
              <text
                key={loc.id + "-label"}
                x={cx}
                y={cy}
                textAnchor="middle"
                fontFamily="Fraunces, serif"
                fontStyle="italic"
                fontSize="20"
                fill="#3a4a3f"
                opacity="0.7"
                pointerEvents="none"
              >
                {loc.name}
              </text>
            );
          }

          if (loc.name === "Patio") {
            return (
              <text
                key={loc.id + "-label"}
                x={cx}
                y={cy}
                textAnchor="middle"
                fontFamily="Caveat, cursive"
                fontSize="14"
                fill="#3d3026"
                pointerEvents="none"
              >
                {loc.name}
              </text>
            );
          }

          if (loc.kind === "container") {
            return null;
          }

          const w = bbox.maxX - bbox.minX;
          const h = bbox.maxY - bbox.minY;
          const dominantHorizontal = w >= h;
          let edge: "top" | "bottom" | "left" | "right";
          if (dominantHorizontal) {
            edge = bbox.minY <= vh - bbox.maxY ? "top" : "bottom";
          } else {
            edge = bbox.minX <= vw - bbox.maxX ? "left" : "right";
          }

          if (edge === "top") {
            return (
              <g key={loc.id + "-label"} pointerEvents="none">
                <line
                  x1={cx} y1={bbox.minY - 5}
                  x2={cx} y2={Math.max(bbox.minY - 40, 18)}
                  stroke="#5d4a3a" strokeWidth="0.5"
                />
                <text
                  x={cx}
                  y={Math.max(bbox.minY - 46, 12)}
                  textAnchor="middle"
                  fontFamily="Caveat, cursive"
                  fontSize="20"
                  fontWeight="500"
                  fill="#3d3026"
                >
                  {loc.name}
                </text>
              </g>
            );
          }

          if (edge === "bottom") {
            return (
              <g key={loc.id + "-label"} pointerEvents="none">
                <line
                  x1={cx} y1={bbox.maxY + 5}
                  x2={cx} y2={Math.min(bbox.maxY + 40, vh - 18)}
                  stroke="#5d4a3a" strokeWidth="0.5"
                />
                <text
                  x={cx}
                  y={Math.min(bbox.maxY + 52, vh - 4)}
                  textAnchor="middle"
                  fontFamily="Caveat, cursive"
                  fontSize="20"
                  fontWeight="500"
                  fill="#3d3026"
                >
                  {loc.name}
                </text>
              </g>
            );
          }

          if (edge === "left") {
            return (
              <g key={loc.id + "-label"} pointerEvents="none">
                <line
                  x1={bbox.minX - 5} y1={cy}
                  x2={Math.max(bbox.minX - 40, 22)} y2={cy}
                  stroke="#5d4a3a" strokeWidth="0.5"
                />
                <text
                  x={Math.max(bbox.minX - 46, 18)}
                  y={cy + 4}
                  textAnchor="end"
                  fontFamily="Caveat, cursive"
                  fontSize="16"
                  fill="#3d3026"
                >
                  {loc.name}
                </text>
              </g>
            );
          }

          return (
            <g key={loc.id + "-label"} pointerEvents="none">
              <line
                x1={bbox.maxX + 5} y1={cy}
                x2={Math.min(bbox.maxX + 40, vw - 22)} y2={cy}
                stroke="#5d4a3a" strokeWidth="0.5"
              />
              <text
                x={Math.min(bbox.maxX + 46, vw - 18)}
                y={cy + 4}
                textAnchor="start"
                fontFamily="Caveat, cursive"
                fontSize="16"
                fill="#3d3026"
              >
                {loc.name}
              </text>
            </g>
          );
        })}

        {locationsWithPolygons.length > 0 && isDefaultViewport && (
          <g transform="translate(620, 600)" pointerEvents="none">
            <circle r="20" fill="#faf6ef" stroke="#5d4a3a" strokeWidth="0.7" />
            <text y="-22" textAnchor="middle" fontFamily="Fraunces, serif" fontStyle="italic" fontSize="13" fill="#3d3026">N</text>
            <path d="M 0 -10 L 4 5 L 0 0 L -4 5 Z" fill="#b66b48" />
            <circle r="1.8" fill="#5d4a3a" />
          </g>
        )}
      </svg>
    </div>
  );
}

